<template>
  <div class="support-page">
    <Messages />
    <div v-if="!isReady" class="support-denied">載入中…</div>
    <div v-else class="support-content">
      <h2>i18n開發RD專用支援頁面</h2>
      <p class="support-hint">此頁僅能透過網址 <code>/#/support</code> 進入</p>

      <section class="support-section">
        <h3>面板設定（寫入成功後的刷新模式）</h3>
        <p>
          GET/PUT /panel/setting — 每個操作選一種模式。預設：阻塞 GET（skip=false，reconcile=false）。失敗時仍會立即阻塞
          GET。
        </p>
        <div
          v-for="row in panelActionRows"
          :key="row.action"
          class="support-setting-row"
        >
          <strong>{{ row.label }}</strong>
          <select
            :value="getActionMode(row.action)"
            :disabled="isLoading"
            @change="setActionMode(row.action, $event.target.value)"
          >
            <option
              v-for="opt in actionModeOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
        <div>
          <button type="button" :disabled="isLoading" @click="onSavePanelSetting">儲存面板設定</button>
        </div>
      </section>

      <section class="support-section">
        <h3>Async API flow</h3>
        <p>Enable asynchronous execution independently for each slow write API.</p>
        <div
          v-for="row in asyncActionRows"
          :key="row.field"
          class="support-setting-row async-setting-row"
        >
          <strong>{{ row.label }}</strong>
          <code>{{ row.method }} {{ row.path }}</code>
          <label>
            <input
              v-model="draftPanelSetting[row.field]"
              type="checkbox"
              :disabled="isLoading"
            />
            Async
          </label>
        </div>
        <div>
          <button type="button" :disabled="isLoading" @click="onSavePanelSetting">儲存 Async API 設定</button>
        </div>
      </section>

      <section class="support-section">
        <h3>清除後台全部翻譯快取</h3>
        <p>DELETE /translation/support/cache — 清除本機 memory + Redis <code>i18n-service::translation*</code></p>
        <button type="button" :disabled="isLoading" @click="onClearAllCache">清除全部快取</button>
      </section>

      <section class="support-section">
        <h3>建立 MongoDB 索引</h3>
        <p>
          POST /translation/support/indexes — 先 shared collections，再每個專案collection（需 APPROVE 權限）
        </p>
        <button type="button" :disabled="isLoading" @click="onEnsureMongoIndexes">建立索引</button>
      </section>

      <section class="support-section">
        <h3>清除後台單一專案快取</h3>
        <p>DELETE /translation/:project/cache</p>
        <label>
          專案
          <select v-model="selectedProject">
            <option v-for="p in projects" :key="p" :value="p">{{ p }}</option>
          </select>
        </label>
        <button type="button" :disabled="isLoading || !selectedProject" @click="onClearProjectCache">
          清除專案快取
        </button>
      </section>

      <section class="support-section">
        <h3>緊急支援用 - 硬刪除翻譯 Key（所有版本）</h3>
        <p>DELETE /translation/:project/support — 不可還原，會寫 audit log（需 APPROVE 權限）</p>
        <label>
          專案
          <select v-model="deleteProject">
            <option v-for="p in projects" :key="'hard-' + p" :value="p">{{ p }}</option>
          </select>
        </label>
        <label>
          key
          <input v-model="deleteKey" type="text" placeholder="translation.key" />
        </label>
        <button
          type="button"
          class="danger"
          :disabled="isLoading || !deleteProject || !deleteKeyTrimmed"
          @click="onSupportDeleteKey"
        >
          硬刪除 Key
        </button>
      </section>

      <section class="support-section">
        <h3>軟刪除翻譯 Key</h3>
        <p>DELETE /translation/:project — body: key + ticket（需 DELETE 權限）</p>
        <p v-if="!canDelete" class="support-warn">目前帳號沒有 DELETE 權限，此操作會失敗</p>
        <label>
          專案
          <select v-model="softDeleteProject">
            <option v-for="p in projects" :key="'soft-' + p" :value="p">{{ p }}</option>
          </select>
        </label>
        <label>
          key
          <input v-model="softDeleteKey" type="text" placeholder="translation.key" />
        </label>
        <label>
          ticket
          <input v-model="softDeleteTicket" type="text" placeholder="IN-12345" />
        </label>
        <button
          type="button"
          class="danger"
          :disabled="isLoading || !softDeleteProject || !softDeleteKeyTrimmed || !softDeleteTicketTrimmed"
          @click="onSoftDeleteKey"
        >
          軟刪除 Key
        </button>
      </section>

      <section class="support-section">
        <h3>還原軟刪除 Key</h3>
        <p>POST /translation/:project/revert-delete 與 DELETE /translation/:project/revert-delete（需 DELETE 權限）</p>
        <p v-if="!canDelete" class="support-warn">目前帳號沒有 DELETE 權限，此操作會失敗</p>
        <label>
          專案
          <select v-model="revertProject">
            <option v-for="p in projects" :key="'rev-' + p" :value="p">{{ p }}</option>
          </select>
        </label>
        <label>
          key
          <input v-model="revertKey" type="text" placeholder="translation.key" />
        </label>
        <button
          type="button"
          :disabled="isLoading || !revertProject || !revertKeyTrimmed"
          @click="onRevertDeleteKey('POST')"
        >
          還原 (POST)
        </button>
        <button
          type="button"
          :disabled="isLoading || !revertProject || !revertKeyTrimmed"
          @click="onRevertDeleteKey('DELETE')"
        >
          還原 (DELETE)
        </button>
      </section>

      <div v-if="isLoading" class="loadingOverlay">
        <img src="@/assets/loading.svg" alt="" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Messages from '@/components/Home/Messages.vue'
import { hasSpecifiedPermission, permission } from '@/utils/permission-type'
import { DEFAULT_PANEL_SETTING } from '@/store/helpers'

export default {
  name: 'Support',
  components: { Messages },
  data() {
    return {
      isReady: false,
      selectedProject: '',
      deleteProject: '',
      deleteKey: '',
      softDeleteProject: '',
      softDeleteKey: '',
      softDeleteTicket: '',
      revertProject: '',
      revertKey: '',
      panelActionRows: [
        { action: 'UpdateData', label: '更新翻譯' },
        { action: 'UpdateTicket', label: '更新單號' },
        { action: 'AddData', label: '新增' },
        { action: 'DeleteData', label: '刪除' },
      ],
      actionModeOptions: [
        { value: 'blocking', label: '成功後阻塞 GET（舊行為）' },
        { value: 'skip', label: '略過 GET（僅本地更新）' },
        { value: 'skipReconcile', label: '略過 GET + 背景比對同步' },
      ],
      asyncActionRows: [
        {
          field: 'asyncExcelBatch',
          label: 'Excel batch import',
          method: 'POST',
          path: '/api/v2/excel/batch/:project',
        },
        {
          field: 'asyncTicketApprove',
          label: 'Ticket approval',
          method: 'POST',
          path: '/api/v2/ticket/:project/approve',
        },
        {
          field: 'asyncProductionVersion',
          label: 'Production version promotion',
          method: 'PUT',
          path: '/api/v2/project/:project/productionVersion',
        },
        {
          field: 'asyncEnsureIndexes',
          label: 'Ensure MongoDB indexes',
          method: 'POST',
          path: '/api/v2/translation/support/indexes',
        },
      ],
      draftPanelSetting: { ...DEFAULT_PANEL_SETTING },
    }
  },
  computed: {
    ...mapGetters(['user', 'projects', 'project', 'isLoading', 'panelSetting']),
    canAccess() {
      return this.user?.canAccessSupport === true
    },
    canDelete() {
      return hasSpecifiedPermission(permission.DELETE, this.user?.permissions ?? [])
    },
    deleteKeyTrimmed() {
      return (this.deleteKey || '').trim()
    },
    softDeleteKeyTrimmed() {
      return (this.softDeleteKey || '').trim()
    },
    softDeleteTicketTrimmed() {
      return (this.softDeleteTicket || '').trim()
    },
    revertKeyTrimmed() {
      return (this.revertKey || '').trim()
    },
  },
  async created() {
    await this.$store.dispatch('getUser')
    if (!this.canAccess) {
      if (typeof window !== 'undefined' && window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.replace('/')
      }
      return
    }
    await this.$store.dispatch('getProjects', { currentProject: this.project })
    await this.$store.dispatch('fetchPanelSetting')
    this.draftPanelSetting = { ...this.panelSetting }
    const defaultProject = this.project || this.projects[0] || ''
    this.selectedProject = defaultProject
    this.deleteProject = defaultProject
    this.softDeleteProject = defaultProject
    this.revertProject = defaultProject
    this.isReady = true
  },
  methods: {
    skipKey(action) {
      return `skipGetAfter${action}`
    },
    reconcileKey(action) {
      return `reconcileAfter${action}`
    },
    getActionMode(action) {
      const skip = this.draftPanelSetting[this.skipKey(action)]
      const reconcile = this.draftPanelSetting[this.reconcileKey(action)]
      if (!skip) return 'blocking'
      if (reconcile) return 'skipReconcile'
      return 'skip'
    },
    setActionMode(action, mode) {
      const skip = this.skipKey(action)
      const reconcile = this.reconcileKey(action)
      if (mode === 'blocking') {
        this.$set(this.draftPanelSetting, skip, false)
        this.$set(this.draftPanelSetting, reconcile, false)
        return
      }
      if (mode === 'skip') {
        this.$set(this.draftPanelSetting, skip, true)
        this.$set(this.draftPanelSetting, reconcile, false)
        return
      }
      this.$set(this.draftPanelSetting, skip, true)
      this.$set(this.draftPanelSetting, reconcile, true)
    },
    onSavePanelSetting() {
      this.$store
        .dispatch('updatePanelSetting', { ...this.draftPanelSetting })
        .then((ok) => {
          if (ok) {
            this.draftPanelSetting = { ...this.panelSetting }
          }
        })
    },
    onClearAllCache() {
      if (!window.confirm('確認清除全部 i18n-service 翻譯快取（所有專案）？')) return
      this.$store.dispatch('clearAllTranslationCaches')
    },
    onEnsureMongoIndexes() {
      if (!window.confirm('確認對所有 collection 建立索引？大型 collection 可能需要一段時間。')) return
      this.$store.dispatch('ensureMongoIndexes')
    },
    onClearProjectCache() {
      if (!window.confirm(`確認清除專案「${this.selectedProject}」的翻譯快取？`)) return
      this.$store.dispatch('clearProjectTranslationCache', { project: this.selectedProject })
    },
    onSupportDeleteKey() {
      const key = this.deleteKeyTrimmed
      if (
        !window.confirm(
          `確認硬刪除專案「${this.deleteProject}」的 key「${key}」所有版本？此操作不可還原。`
        )
      ) {
        return
      }
      this.$store.dispatch('supportDeleteTranslationKey', {
        project: this.deleteProject,
        key,
      })
    },
    onSoftDeleteKey() {
      const key = this.softDeleteKeyTrimmed
      const ticket = this.softDeleteTicketTrimmed
      if (
        !window.confirm(
          `確認軟刪除專案「${this.softDeleteProject}」的 key「${key}」(ticket: ${ticket})？`
        )
      ) {
        return
      }
      this.$store.dispatch('softDeleteTranslationKey', {
        project: this.softDeleteProject,
        key,
        ticket,
      })
    },
    onRevertDeleteKey(method) {
      const key = this.revertKeyTrimmed
      if (!window.confirm(`確認以 ${method} 還原專案「${this.revertProject}」的 key「${key}」？`)) {
        return
      }
      this.$store.dispatch('revertDeleteTranslationKey', {
        project: this.revertProject,
        key,
        method,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.support-page {
  padding: 24px;
  max-width: 720px;
}
.support-denied {
  padding: 40px 24px;
  font-size: 16px;
}
.support-hint {
  color: #666;
  font-size: 13px;
}
.support-warn {
  color: #c0392b;
  font-size: 13px;
}
.support-section {
  margin-top: 28px;
  padding-top: 16px;
  border-top: 1px solid #ddd;

  h3 {
    margin: 0 0 8px;
  }

  p {
    margin: 0 0 12px;
    color: #555;
    font-size: 13px;
  }

  label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-right: 12px;
    margin-bottom: 8px;
  }

  .support-setting-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 16px;
    margin-bottom: 10px;

    strong {
      min-width: 72px;
    }

    select {
      min-width: 260px;
      flex: 1;
      max-width: 420px;
    }

    code {
      flex: 1;
      overflow-wrap: anywhere;
    }
  }

  .async-setting-row {
    strong {
      min-width: 180px;
    }

    label {
      margin: 0;
    }

    input {
      min-width: auto;
    }
  }

  input,
  select {
    min-width: 180px;
    padding: 4px 6px;
  }

  button {
    margin-top: 4px;
    margin-right: 8px;
    padding: 6px 12px;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    &.danger {
      background: #c0392b;
      color: #fff;
      border: none;
    }
  }
}
.loadingOverlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
</style>
